import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Callout, Card, TextField } from "@powwow/ui";
import {
  consumeMagicLink,
  devSignIn,
  fetchMe,
  forgotPassword,
  passwordSignIn,
  requestMagicLink,
  describeError,
} from "../api.js";
import { PlatformFooter } from "../platform-footer.js";

/**
 * Sign-in.
 *
 * **Password is the front door.** Staff sign in here every event weekend, and a system that can only
 * be entered by fetching a fresh link from an inbox is one that fails at a gate with no signal. A
 * password is the credential that works when the email does not.
 *
 * The other two ways in are still here, because each answers something a password does not:
 *
 * - **A one-tap link**, for someone who has forgotten their password and has their phone, and for
 *   the first minute of an account's life before a password exists.
 * - **The dev form**, development only, so the seeded accounts stay reachable without an inbox.
 *
 * A `?token=` in the URL is a link arriving, and it takes over the screen — that person has already
 * decided how they are signing in.
 */
export function SignInScreen() {
  const [params] = useSearchParams();
  const token = params.get("token");

  if (token) return <MagicLinkSignIn token={token} />;
  return <PasswordSignIn />;
}

type Mode = "password" | "link" | "forgot";

function PasswordSignIn() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "password") {
        const { mustReset } = await passwordSignIn({ email: email.trim(), password });
        // A password somebody else chose gets them exactly one place: the screen where they
        // replace it.
        navigate(mustReset ? "/set-password" : "/", { replace: true });
        return;
      }
      if (mode === "link") await requestMagicLink(email.trim());
      else await forgotPassword(email.trim());
      setSent(true);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <SignInFrame>
        <Card className="p-6">
          <h2 className="text-title font-black">Check your email</h2>
          <p className="mt-2 text-muted">
            If <strong>{email.trim()}</strong> has an account, a{" "}
            {mode === "link" ? "sign-in link" : "reset link"} is on its way. It expires shortly, so
            use it soon.
          </p>
          <Button
            variant="secondary"
            className="mt-5 w-full"
            onClick={() => {
              setSent(false);
              setMode("password");
            }}
          >
            Back to sign in
          </Button>
        </Card>
      </SignInFrame>
    );
  }

  const heading =
    mode === "password" ? "Sign in" : mode === "link" ? "Email me a link" : "Reset your password";

  return (
    <SignInFrame>
      <Card className="p-6">
        <h2 className="text-title font-black">{heading}</h2>
        <p className="mt-1 text-muted">
          {mode === "password"
            ? "Run today’s event without losing the larger setup."
            : "We’ll send a link to your email address."}
        </p>

        {error && (
          <Callout label="Problem" tone="danger" className="mt-4">
            {error}
          </Callout>
        )}

        <form onSubmit={submit} className="mt-5 space-y-4">
          <TextField
            label="Email address"
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(event) => setEmail(event.target.value)}
          />
          {mode === "password" && (
            <TextField
              label="Password"
              type="password"
              value={password}
              autoComplete="current-password"
              required
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
          <Button
            type="submit"
            busy={busy}
            disabled={!email.trim() || (mode === "password" && !password)}
            className="w-full"
          >
            {busy ? "Working…" : mode === "password" ? "Sign in" : "Send the link"}
          </Button>
        </form>

        <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 border-t border-line pt-4 text-caption font-bold">
          {mode !== "password" && (
            <button
              type="button"
              className="text-brand hover:underline"
              onClick={() => setMode("password")}
            >
              Use a password
            </button>
          )}
          {mode !== "forgot" && (
            <button
              type="button"
              className="text-muted hover:text-ink hover:underline"
              onClick={() => setMode("forgot")}
            >
              Forgot your password?
            </button>
          )}
          {mode !== "link" && (
            <button
              type="button"
              className="text-muted hover:text-ink hover:underline"
              onClick={() => setMode("link")}
            >
              Email me a link instead
            </button>
          )}
        </div>

        {import.meta.env.DEV && <DevSignIn onDone={() => navigate("/", { replace: true })} />}
      </Card>
    </SignInFrame>
  );
}

/** Exchange the token, then route by who the person turns out to be. */
function MagicLinkSignIn({ token }: { token: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await consumeMagicLink(token);
        const me = await fetchMe();
        if (cancelled) return;
        // A platform admin with no community of their own has nowhere else to be — send them to
        // the console. Anyone with a community lands in the workspace, which picks the community.
        const destination =
          me.authenticated && me.platformAdmin && me.communities.length === 0
            ? "/admin/communities"
            : "/";
        navigate(destination, { replace: true });
      } catch (cause) {
        if (!cancelled) setError(describeError(cause));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <SignInFrame>
      <Card className="p-6">
        {error ? (
          <>
            <h2 className="text-title font-black">That link didn’t work</h2>
            <Callout label="Problem" tone="danger" className="mt-4">
              {error}
            </Callout>
            <p className="mt-4 text-muted">
              Sign-in links are single use and expire. Sign in with your password, or ask for a new
              link.
            </p>
            <Button className="mt-4 w-full" onClick={() => navigate("/sign-in", { replace: true })}>
              Back to sign in
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="size-5 animate-spin rounded-full border-2 border-line border-t-brand"
            />
            <p className="font-bold">Signing you in…</p>
          </div>
        )}
      </Card>
    </SignInFrame>
  );
}

/** The seeded-account form. Development only — the server refuses it in production regardless. */
function DevSignIn({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("organiser@example.test");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await devSignIn(email.trim());
      onDone();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 border-t border-line pt-5">
      <Callout label="Development" tone="warning" className="mb-3">
        Seeded accounts, development only. Try <code>superadmin@example.test</code> for the platform
        console.
      </Callout>
      {error && (
        <Callout label="Problem" tone="danger" className="mb-3">
          {error}
        </Callout>
      )}
      <form onSubmit={submit} className="flex items-end gap-2">
        <TextField
          label="Seeded account"
          type="email"
          value={email}
          className="flex-1"
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" variant="secondary" busy={busy}>
          Go
        </Button>
      </form>
    </div>
  );
}

/** The shared marketing frame every state sits inside. */
function SignInFrame({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <main className="grid flex-1 place-items-center px-4 py-10">
        <div className="grid w-full max-w-5xl gap-8 lg:grid-cols-[1fr_420px] lg:items-center">
          <section>
            <p className="mb-3 text-caption font-black tracking-wide text-brand uppercase">
              Event operations
            </p>
            <h1 className="max-w-2xl">
              <img src="/brand/logo-wordmark.png" alt="PowWow" className="h-16 w-auto sm:h-20" />
            </h1>
            <p className="mt-4 max-w-xl text-body-large text-muted">
              A focused command centre for registrations, check-in, judging, results and payouts.
            </p>
            <div className="mt-8 grid max-w-2xl gap-3 sm:grid-cols-3">
              {["Desk ready", "Arena live", "Payout clear"].map((label) => (
                <div
                  key={label}
                  className="rounded-lg border border-white/75 bg-paper/80 p-4 font-bold shadow-sm ring-1 ring-line/60"
                >
                  {label}
                </div>
              ))}
            </div>
          </section>

          {children}
        </div>
      </main>
      <PlatformFooter withLogos />
    </div>
  );
}
